import { NavLink } from 'react-router-dom'
import { useAuth } from '../../../hooks/useAuth'

/**
 * Report Panel sidebar.
 *
 * Reports used to sit under the device-list sidebar, which was inherited
 * chrome rather than a decision: a monthly volume report is about a group and a
 * period, and picking a RIC does nothing to it. This replaces that list with a
 * panel of the things a report actually has — the reports themselves, the terms
 * they are produced under, and (for an administrator) the configuration.
 *
 * Grouped by what the reports are about rather than by who may touch them, so
 * a second meter type later becomes a second section rather than a rewrite.
 * Same shape as the admin sidebar deliberately: the configuration page moved
 * here from the Admin Panel, and an administrator following it should
 * recognise where they arrived rather than find a second dialect.
 */

interface NavItem {
  to: string
  label: string
  end?: boolean
  hint?: string
}

interface NavSection {
  heading?: string
  items: NavItem[]
}

function NavItems({ items }: { items: NavItem[] }) {
  return (
    <>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          title={item.hint}
          className={({ isActive }) =>
            `flex items-center px-4 py-2 rounded-lg transition whitespace-nowrap ${
              isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
            }`
          }
        >
          <span className="flex-1">{item.label}</span>
        </NavLink>
      ))}
    </>
  )
}

export default function ReportsNav() {
  const { isAdmin } = useAuth()

  const flowmeter: NavItem[] = [
    { to: '/reports/flowmeter', label: 'Reports', end: true, hint: 'Every report issued for your groups' },
  ]

  // Generation is where reports are produced and the schedule is set — an
  // administrator's page, and listed first for them since producing is what
  // they come here to do. A district reads its reports and the terms they are
  // produced under; it does not run them.
  if (isAdmin()) {
    flowmeter.unshift({
      to: '/reports/flowmeter/generation',
      label: 'Generation',
      hint: 'Produce a report, and set the schedule it runs on',
    })
  } else {
    flowmeter.push({
      to: '/reports/settings',
      label: 'Settings',
      hint: 'How your report is produced',
    })
  }

  const sections: NavSection[] = [{ heading: 'Flowmeters', items: flowmeter }]

  return (
    <nav className="space-y-4 p-4">
      {sections.map((section, idx) => (
        <div key={idx} className="space-y-1">
          {section.heading && (
            <p className="px-4 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {section.heading}
            </p>
          )}
          <NavItems items={section.items} />
        </div>
      ))}
    </nav>
  )
}
